import { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import InputComposer, { InputComposerRef } from '@components/DesignLibrary/InputComposer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import uniqBy from 'lodash/uniqBy';
import createRichTextAst from '@helpers/createRichTextAst';
import Message from '@components/DesignLibrary/Message';
import Spacer from '@components/Spacer';
import { ArrowBendDownRight } from '@phosphor-icons/react';
import Buttons from '@components/Buttons/Buttons';
import XCircle from '@components/Icons/XCircle';
import ProgressIndicator from '@components/ProgressIndicator';
import PaginationObserver from '@components/PaginationObserver';
import PencilSimple from '@components/Icons/PencilSimple';
import Trash from '@components/Icons/Trash';
import removeNullish from '@helpers/removeNullish';
import { useIsMobile, useUser } from '@hooks';
import { nanoid } from 'nanoid';
import { JobType, useJobContext } from '@providers/Jobs';
import pluralize from 'pluralize';
import { useRouter } from 'next/router';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import Button from '@components/DesignLibrary/Button';
import Note from '@components/Icons/Note';
import Users from '@components/Icons/Users';
import Calendar from '@components/Icons/Calendar';
import * as format from '@format';
import useUserWorkflowContext, { WorkflowCollaborator } from '@/hooks/useUserWorkflowContext';
import useSocketContext from '@providers/Socket/useSocketContext';
import { SocketMessageType } from '@providers/Socket/SocketContext';
import { useOpenDocumentRequestUserPane, useOpenDocumentRequestUserFilePane } from '@panes/hooks';

import { useQueryState } from 'nuqs';
import { queryParam, queryStateParser } from '@/panes/queryStateParser';
import s from './UserWorkflowDiscussion.module.scss';

type Props = {
  userWorkflow: Pick<ApiModels.UserWorkflow, 'id'> & {
    workflow: Pick<ApiModels.Workflow, 'id'> & {
      creator: Pick<ApiModels.Creator, 'id' | 'slug' | 'personId'>;
    };
    workflowConversation: Pick<ApiModels.WorkflowConversation, 'id' | 'rootMessageId'> | null;
  };
  isAsync?: boolean;
  isLoaded: boolean;
  contextId?: string;
  contextType?: string;
  isInWorkflow?: boolean;
  loadedCollaborators?: WorkflowCollaborator[];
  rejectionComment?: Awaited<ReturnType<typeof tsClient.workflowConversations.createWorkflowConversationMessage>>
};

const PAGE_SIZE = 10;
const SCROLL_DISTANCE_CONSIDERED_TO_BE_BOTTOM = 15;

type Actions = 'Reply' | 'Edit' | 'Delete';

const UserWorkflowDiscussion: FC<Props> = ({
  isLoaded,
  userWorkflow,
  isAsync = false,
  contextId,
  contextType,
  isInWorkflow,
  rejectionComment,
  loadedCollaborators = [],
}) => {
  const { collaborators } = useUserWorkflowContext();

  // todo - lift up state so messages don't have to be refetched upon switching tabs
  const [messages, setMessages] = useState<Awaited<ReturnType<typeof tsClient.workflowConversations.getWorkflowConversationMessages>>['items']>([]);
  const [page, setPage] = useState(1);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isFirstPageLoaded, setFirstPageLoaded] = useState(false);
  const [numMessages, setNumMessages] = useState(0);
  const [editId, setEditId] = useState<string | null>(null);
  const [jobCorrelationId, setJobCorrelationId] = useState(nanoid());
  const [isFetched, setIsFetched] = useState(false);
  const [queryState] = useQueryState(queryParam, queryStateParser);
  const router = useRouter();

  const isMobile = useIsMobile();

  const queryThreadId = deserializeQueryStringItem(router.query.threadId, { type: 'string' });
  const queryCommentId = deserializeQueryStringItem(router.query.commentId, { type: 'string' });

  const openDocumentRequestUserPane = useOpenDocumentRequestUserPane();
  const openDocumentRequestFileUserPane = useOpenDocumentRequestUserFilePane();

  const { addJob, jobs } = useJobContext();

  const uploadJobs = jobs.filter(j => j.correlationId === jobCorrelationId);

  const [user] = useUser();

  const { addListener, removeListener, isInitialized: isSocketInitialized, joinChannels } = useSocketContext();

  const inputComposerRef = useRef<InputComposerRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const desiredScrollFromBottom = useRef<number | null>(null);

  const { getData: getMessages } = useNoodleApi(tsClient.workflowConversations.getWorkflowConversationMessages);
  const { getData: sendMessage } = useNoodleApi(tsClient.workflowConversations.createWorkflowConversationMessage);
  const { getData: updateMessage } = useNoodleApi(tsClient.workflowConversations.updateWorkflowConversationMessage);
  const { getData: fetchMessageById } = useNoodleApi(tsClient.workflowConversations.getWorkflowConversationMessageById);
  const { getData: deleteMessage } = useNoodleApi(tsClient.workflowConversations.deleteWorkflowConversationMessage);

  const fetchData = useCallback(async () => {
    setIsFetching(true);
    const { data } = await getMessages({
      contextId,
      page,
      perPage: PAGE_SIZE,
      userWorkflowId: userWorkflow.id,
    });
    if (data?.items) {
      setMessages(prev => uniqBy([...data.items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()), ...prev], 'id'));
      setNumMessages(data.numItems);
    }
    setIsFetching(false);
    if (!isFirstPageLoaded) {
      setTimeout(() => {
        const rightPanel = document.getElementById('rightPanel') || document.getElementById('modal-body');
        if (rightPanel) {
          rightPanel.scroll(0, rightPanel.scrollHeight);
        }
        setFirstPageLoaded(true);
      }, 100);
    } else {
      setTimeout(() => {
        const rightPanel = document.getElementById('rightPanel') || document.getElementById('modal-body');
        if (rightPanel) {
          const newScroll = rightPanel.scrollHeight - rightPanel.getBoundingClientRect().height - (desiredScrollFromBottom.current || 0);
          rightPanel.scrollTo(0, newScroll);
        }
      }, 50);
    }
  }, [page, userWorkflow, getMessages, isLoaded, contextId, rejectionComment, queryState]);

  useEffect(() => {
    if (!isLoaded) {
      setMessages([]);
    }
  }, [isLoaded]);

  useEffect(() => {
    // If a thread ID is specified in the query string, keep fetching pages until all messages are fetched or thread is found
    if (queryThreadId) {
      if (!isFetching && isFirstPageLoaded && messages.find(m => m.id === queryThreadId)) {
        setIsFetched(true);
        setThreadId(queryThreadId);
      } else if (!isFetching && isFirstPageLoaded && numMessages !== messages.length) {
        setPage(prev => prev + 1);
      } else if (!isFetching && isFirstPageLoaded && numMessages === messages.length) {
        setIsFetched(true);
      }
    } else if (!queryThreadId && isFirstPageLoaded) {
      setIsFetched(true);
    }
  }, [queryThreadId, isFetching, messages, isFirstPageLoaded, numMessages]);

  useEffect(() => {
    // If a comment ID is specified in the query string without a thread id, keep fetching pages until all messages are fetched or comment is found
    if (queryCommentId && !queryThreadId) {
      if (!isFetching && isFirstPageLoaded && messages.find(m => m.id === queryCommentId)) {
        setIsFetched(true);
      } else if (!isFetching && isFirstPageLoaded && numMessages !== messages.length) {
        setPage(prev => prev + 1);
      } else if (!isFetching && isFirstPageLoaded && numMessages === messages.length) {
        setIsFetched(true);
      }
    } else if (!queryCommentId && !queryThreadId && isFirstPageLoaded) {
      setIsFetched(true);
    }
  }, [queryCommentId, queryThreadId, isFetching, messages, isFirstPageLoaded, numMessages]);

  useEffect(() => {
    if (queryCommentId && isFetched && !isFetching) {
      setTimeout(() => {
        const rightPanel = document.getElementById('rightPanel') || document.getElementById('modal-body');
        if (rightPanel) {
          const message = document.getElementById(queryCommentId);
          if (message) {
            rightPanel.scrollTo(0, message.offsetTop - 75);
          }
        }
      }, 100);
    }
  }, [isFetched, queryCommentId, isFetching, messages]);

  useEffect(() => {
    if (isLoaded) {
      fetchData();
    }
  }, [fetchData, isLoaded]);

  const handleChangeIsEdit = (isEdit: boolean): void => {
    if (!isEdit) {
      setEditId(null);
    }
  };

  const addComment = async (comment: Awaited<ReturnType<typeof tsClient.workflowConversations.createWorkflowConversationMessage>>): Promise<void> => {
    if (threadId) {
      setMessages(prev =>
        prev.map(message => {
          if (message.id === threadId) {
            return {
              ...message,
              children: [...message.children, comment],
            };
          }
          return message;
        }),
      );
    } else {
      setNumMessages(prev => prev + 1);
      setMessages(prev => uniqBy([...prev, comment], 'id'));
    }
    setTimeout(() => {
      const rightPanel = document.getElementById('rightPanel') || document.getElementById('modal-body');
      if (rightPanel) {
        const element = document.getElementById(comment.id);
        const inputComposer = document.getElementById('inputComposer');
        if (element && inputComposer) {
          const isInView = element.offsetTop >= rightPanel.scrollTop
            && element.offsetTop + element.clientHeight + inputComposer.clientHeight <= rightPanel.scrollTop + rightPanel.clientHeight;
          if (!isInView) {
            rightPanel.scroll(0, rightPanel.scrollHeight);
          }
        }
      }
    }, 100);
  };

  const handleSubmit = async ({ text, medias }: { text: string; medias: { id: string }[] }): Promise<void> => {
    setIsSendingMessage(true);

    const parentMessage = threadId ? messages.find(msg => msg.id === threadId) : null;

    const messageContextId = parentMessage?.contextId || contextId;

    const messageContextType = parentMessage?.contextType || contextType;

    const sendFunction = async (overrideMediaIds?: { id: string }[]): Promise<void> => {
      if (!editId) {
        const { data: comment } = await sendMessage({
          contextId: messageContextId,
          contextType: messageContextType,
          medias: overrideMediaIds,
          parentId: threadId || undefined,
          text: createRichTextAst(text),
          userWorkflowId: userWorkflow.id,
        });
        if (comment) {
          addComment(comment);
        }
      } else {
        const { data: comment } = await updateMessage({
          messageId: editId,
          text: createRichTextAst(text),
          userWorkflowId: userWorkflow.id,
        });
        if (comment) {
          if (threadId) {
            setMessages(prev =>
              prev.map(message => {
                if (message.id === editId) {
                  return { ...message, ...comment };
                }
                if (message.id === threadId) {
                  return {
                    ...message,
                    children: message.children.map(c => (c.id === editId ? comment : c)),
                  };
                }
                return message;
              }),
            );
          } else {
            setMessages(prev => prev.map(c => (c.id === editId ? comment : c)));
          }
        }
      }
      setIsSendingMessage(false);
    };
    if (isAsync) {
      addJob({
        dependsOn: uploadJobs,
        function: sendFunction,
        title: 'Comment Reply',
        type: JobType.COMMENT_REPLY,
      });
    } else {
      await sendFunction(medias);
    }
    setJobCorrelationId(nanoid());
  };

  const handleDeleteMessage = async (messageId: string): Promise<void> => {
    await deleteMessage({
      messageId,
      userWorkflowId: userWorkflow.id,
    });

    setMessages(prevMessages =>
      prevMessages
        .filter(m => m.id !== messageId)
        .map(m => ({
          ...m,
          children: m.children.filter(child => child.id !== messageId),
        })),
    );
  };

  const handleMessageAction = ({ action, message }: { action: Actions; message: Pick<ApiModels.Message, 'id' | 'text'> }): void => {
    if (action === 'Reply') {
      setThreadId(message.id);
    } else if (action === 'Edit') {
      setEditId(message.id);
      inputComposerRef.current?.setValue?.({ text: message.text });
      inputComposerRef.current?.setIsEdit?.(true);
    } else if (action === 'Delete') {
      handleDeleteMessage(message.id);
    }
  };

  const handleFetchNextPage = (): void => {
    if (isFetching) {
      return;
    }
    setPage(prev => prev + 1);
  };

  const threadMessage = messages.find(m => m.id === threadId);

  const isPaginationEnabled = isFirstPageLoaded && isFetched && numMessages !== messages.length;

  useEffect(() => {
    const rightPanel = document.getElementById('rightPanel') || document.getElementById('modal-body');
    if (rightPanel) {
      const scrollListener = (): void => {
        const fromBottom = rightPanel.scrollHeight - (rightPanel.scrollTop + rightPanel.getBoundingClientRect().height);
        desiredScrollFromBottom.current = Math.abs(rightPanel.scrollHeight - fromBottom) < SCROLL_DISTANCE_CONSIDERED_TO_BE_BOTTOM ? 0 : fromBottom;
      };
      rightPanel.addEventListener('scroll', scrollListener);
      return () => rightPanel.removeEventListener('scroll', scrollListener);
    }
    return () => {};
  });

  useEffect(() => {
    if (contextId) {
      if (messages.length > 0) {
        setThreadId(messages.find(m => m.contextId === contextId)?.id ?? null);
      } else {
        setThreadId(null);
      }
    }
  }, [contextId, messages]);

  useEffect(() => {
    if (isSocketInitialized && userWorkflow.workflowConversation) {
      joinChannels([`workflowConversation/${userWorkflow.workflowConversation.id}`]);
    }
  }, [isSocketInitialized, userWorkflow.workflowConversation, joinChannels]);

  useEffect(() => {
    const handleNewWorkflowMessage = async (data: unknown): Promise<void> => {
      const {
        authorId,
        contextId: incomingContextId,
        messageId,
        parentId,
        rootMessageId,
      } = data as {
        authorId: string;
        contextId: string;
        messageId: string;
        parentId: string;
        rootMessageId: string;
      };
      if (authorId === user?.id || incomingContextId !== contextId || rootMessageId !== userWorkflow.workflowConversation?.rootMessageId) {
        return;
      }

      const { data: fetchedMessage } = await fetchMessageById({
        messageId,
        userWorkflowId: userWorkflow.id,
      });

      if (fetchedMessage && !messages.find(msg => msg.id === messageId)) {
        if (rootMessageId === parentId) {
          setNumMessages(prev => prev + 1);

          setMessages(prev => uniqBy([...prev, fetchedMessage], 'id'));
        } else {
          setMessages(prev =>
            prev.map(message => {
              if (message.id === parentId) {
                return {
                  ...message,
                  children: uniqBy([...message.children, fetchedMessage], 'id'),
                };
              }
              return message;
            }),
          );
        }
      }
    };

    const listenerId = addListener({
      fn: handleNewWorkflowMessage,
      messageType: SocketMessageType.NEW_WORKFLOW_DISCUSSION_MESSAGE,
    });

    return () => {
      removeListener(listenerId);
    };
  }, [addListener, removeListener, user?.id, userWorkflow, getMessages, contextId]);

  if (!userWorkflow.workflowConversation) {
    return null;
  }

  const renderMessage = (
    m: Awaited<ReturnType<typeof tsClient.workflowConversations.getWorkflowConversationMessages>>['items'][number],
  ): ReactElement => (
    <div className={s.message} id={m.id} style={queryCommentId === m.id ? { backgroundColor: 'rgba(0, 109, 255, 0.10)' } : {}}>
      <Message
        creator={userWorkflow.workflow.creator}
        isAi={m.isAIGenerated}
        timestamp={m.createdAt}
        message={m}
        compact
        user={m.owner}
        secondaryActions={{
          ariaLabel: 'Message actions',
          onSelect: (action: string): void => handleMessageAction({ action: action as Actions, message: m }),
          options: [
            m.type !== ApiModels.MessageType.WorkflowInternalNote && queryCommentId !== m.id
              ? {
                icon: ArrowBendDownRight,
                value: 'Reply',
              }
              : null,
            m.owner.id === user?.id
              ? {
                icon: PencilSimple,
                value: 'Edit',
              }
              : null,
            m.owner.id === user?.id
              ? {
                destructive: true,
                icon: Trash,
                value: 'Delete',
              }
              : null,
          ].filter(removeNullish),
        }}
        replies={m.children}
        isReplyView={false}
      />
      {queryCommentId === m.id && (
        <>
          <Spacer size={8} />
          <Button onClick={() => handleMessageAction({ action: 'Reply', message: m })} size="xs" variant="primary">
            Reply in thread
          </Button>
          <Spacer size={16} />
        </>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className={s.discussion}>
      {(!isFetched || !isLoaded) && (
        <>
          <Spacer />
          <ProgressIndicator isCentered />
          <Spacer />
        </>
      )}
      {isFetched && isLoaded && (
        <>
          {(threadMessage || messages.length > 0) && (
            <div className={s.messages}>
              {!contextId && threadMessage && (
                <div className={s.threadHeader}>
                  <p className="body-md-bold">Thread</p>
                  <Buttons isWrapper onClick={() => setThreadId(null)}>
                    <XCircle weight="fill" color="var(--color-gray-75)" size={24} />
                  </Buttons>
                </div>
              )}

              {threadMessage
                ? (
                  <>
                    <div className={s.message}>
                      {threadMessage.contextId && isInWorkflow && (
                        <Button
                          variant="secondary"
                          size="sm"
                          fullWidth
                          className={s.contextButton}
                          onClick={() => {
                            if (threadMessage.contextType === 'document-request' && threadMessage.contextId) {
                              openDocumentRequestUserPane({ documentRequestUserId: threadMessage.contextId });
                            }
                            if (threadMessage.contextType === 'document-request-file' && threadMessage.contextId) {
                              openDocumentRequestFileUserPane({ documentRequestUserFileId: threadMessage.contextId });
                            }
                          }}
                        >
                          Open {threadMessage.contextTitle}
                        </Button>
                      )}
                      <Message
                        creator={userWorkflow.workflow.creator}
                        isAi={threadMessage.isAIGenerated}
                        timestamp={threadMessage.createdAt}
                        message={threadMessage}
                        user={threadMessage.owner}
                        compact
                        secondaryActions={
                          threadMessage.owner.id === user?.id
                            ? {
                              ariaLabel: 'Message actions',
                              onSelect: (action: string): void =>
                                handleMessageAction({ action: action as Actions, message: threadMessage }),
                              options: [
                                {
                                  icon: PencilSimple,
                                  value: 'Edit',
                                },
                                {
                                  destructive: true,
                                  icon: Trash,
                                  value: 'Delete',
                                },
                              ],
                            }
                            : undefined
                        }
                      />
                      <Spacer size="24px" isLine />
                      <p className="caption" style={{ color: 'var(--color-gray-75)' }}>
                        {threadMessage.children.length} {pluralize('reply', threadMessage.children.length || 0)}
                      </p>
                    </div>
                    {threadMessage.children.map(m => {
                      const secondaryActions = m.owner.id === user?.id
                        ? {
                          ariaLabel: 'Message actions',
                          onSelect: (action: string): void => handleMessageAction({ action: action as Actions, message: m }),
                          options: [
                            {
                              icon: PencilSimple,
                              value: 'Edit',
                            },
                            {
                              destructive: true,
                              icon: Trash,
                              value: 'Delete',
                            },
                          ],
                        }
                        : undefined;

                      return (
                        <div
                          key={m.id}
                          id={m.id}
                          className={s.message}
                          style={queryCommentId === m.id ? { backgroundColor: 'rgba(0, 109, 255, 0.10)' } : {}}
                        >
                          <Message
                            creator={userWorkflow.workflow.creator}
                            isAi={m.isAIGenerated}
                            timestamp={m.createdAt}
                            message={m}
                            user={m.owner}
                            compact
                            secondaryActions={secondaryActions}
                          />
                        </div>
                      );
                    })}
                  </>
                )
                : (
                  <>
                    {isPaginationEnabled && (
                      <PaginationObserver
                        showSpinner={numMessages !== messages.length}
                        intersectTime={1000}
                        isDisabled={false}
                        loadFn={handleFetchNextPage}
                      />
                    )}
                    {messages.map(m => (
                      <div key={m.id} className={m.type === ApiModels.MessageType.WorkflowInternalNote ? s.internalNoteWrapper : ''}>
                        <div className={m.type === ApiModels.MessageType.WorkflowInternalNote ? s.internalNote : ''}>
                          {m.type === ApiModels.MessageType.WorkflowInternalNote && (
                            <div className={s.noteHeader}>
                              <p className="caption" style={{ color: 'var(--color-gray-75)' }}>
                              Internal note
                              </p>
                              <div className={s.icon}>
                                <Note color="var(--color-gray-75)" size={16} weight="fill" />
                              </div>
                            </div>
                          )}
                          {renderMessage(m)}
                          {m.type === ApiModels.MessageType.WorkflowInternalNote && m.metadata?.contactType && (
                            <div className={s.metadata}>
                              <Users size={16} />
                              <p className="caption">Contacted via {m.metadata.contactType}</p>
                            </div>
                          )}
                          {m.type === ApiModels.MessageType.WorkflowInternalNote && m.metadata?.date && m.metadata?.time && (
                            <div className={s.metadata}>
                              <Calendar size={16} />
                              <p className="caption">
                                {format.datetimeLocalTZ.abbreviated(m.metadata.date as string)}, {m.metadata.time}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
            </div>
          )}
          {!threadMessage && messages.length === 0 && (
            <div className={s.emptyState}>
              <p className="body-sm">No messages yet</p>
            </div>
          )}
          <Spacer />
          <div id="inputComposer" className={s.inputComposer} style={isMobile ? { paddingBottom: 76 } : undefined}>
            <InputComposer
              attachmentTypes={['File', 'Photo', 'Video', 'Audio']}
              ref={inputComposerRef}
              isSending={isSendingMessage}
              onSubmit={handleSubmit}
              jobCorrelationId={jobCorrelationId}
              referenceId={userWorkflow.workflowConversation.rootMessageId || ''}
              referenceType="chat-message"
              isAsync={isAsync || false}
              onChangeIsEdit={handleChangeIsEdit}
              collaborators={collaborators?.length ? collaborators : loadedCollaborators}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UserWorkflowDiscussion;
